import React, { useState, useEffect } from 'react';
import { DhabaCard, LocationSearchBar } from '../components';
import { useLocation } from '../store';
import { getNearbyDhabas } from '../services/dhabaService';

/**
 * Food Screen - Shows nearby dhabas/restaurants based on selected location
 */
export function FoodScreen({ onBack, onSelectDhaba }) {
  const [dhabas, setDhabas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Use location from context (either current GPS or user-selected)
  const { coords, locationName, isLoadingCurrent } = useLocation();


  // Fetch dhabas when location is available
  useEffect(() => {
    async function fetchDhabas() {
      if (!coords || isLoadingCurrent) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const filterOptions = {};
        if (filter === 'Pure Veg') filterOptions.vegOnly = true;
        if (filter === 'Parking') filterOptions.hasParking = true;
        
        const data = await getNearbyDhabas(coords, 100, filterOptions);
        setDhabas(data);
      } catch (err) {
        setError('Failed to load restaurants. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchDhabas();
  }, [coords, filter, isLoadingCurrent]);

  // Filter dhabas by search query and filter type
  const filteredDhabas = dhabas.filter(dhaba => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = dhaba.name.toLowerCase().includes(query);
      const matchesCuisine = dhaba.cuisine?.some(c => c.toLowerCase().includes(query));
      if (!matchesName && !matchesCuisine) return false;
    }
    
    // Tag-based filters
    if (filter === 'Pure Veg') {
      return dhaba.tags?.includes('Pure Veg');
    }
    if (filter === 'Non-Veg') {
      return !dhaba.tags?.includes('Pure Veg');
    }
    if (filter === 'Open 24x7') {
      return dhaba.timings?.open_24x7 || dhaba.tags?.includes('Open 24x7');
    }
    if (filter === 'AC Dining') {
      return dhaba.amenities?.ac_dining || dhaba.tags?.includes('AC Dining');
    }
    if (filter === 'Parking') {
      return dhaba.amenities?.parking;
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header with back button, title and location selector */}
      <div className="bg-white p-4 flex items-center gap-3 border-b sticky top-0 z-20">
        <button 
          onClick={onBack} 
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#000000', padding: '4px' }}
          data-testid="food-back-btn"
        >
          &lt;
        </button>
        <span className="text-lg">🍽️</span>
        <h1 className="text-lg font-bold">Food & Restaurants</h1>
        <div className="ml-auto">
          <LocationSearchBar />
        </div>
      </div>
      
      <div className="p-4">
        {/* Search Bar */}
        <div style={{ backgroundColor: '#F3F4F6', borderRadius: '9999px', display: 'flex', alignItems: 'center', padding: '12px 16px', border: 'none' }}>
          <span className="text-gray-400 mr-2">🔍</span>
          <input 
            placeholder="Search restaurants or dishes..." 
            className="flex-1 text-sm"
            style={{ backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="food-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                width: '18px',
                height: '18px',
                minWidth: '18px',
                minHeight: '18px',
                backgroundColor: '#9CA3AF',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: '10px',
                border: 'none',
                cursor: 'pointer'
              }}
              data-testid="food-search-clear"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filters - Icons with text below on light gradient background */}
        <div 
          className="mt-4 flex gap-6 overflow-x-auto pb-3 pt-3 px-2 scrollbar-hide"
          style={{ 
            background: 'linear-gradient(135deg, #F0FDF4 0%, #FEFCE8 100%)',
            borderRadius: '16px',
            marginLeft: '-8px',
            marginRight: '-8px',
            paddingLeft: '16px',
            paddingRight: '16px'
          }}
        >
          {/* All */}
          <div
            onClick={() => setFilter('All')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-all"
          >
            <span style={{ fontSize: '24px' }}>🍽️</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'All' ? '#EA580C' : '#374151', fontWeight: filter === 'All' ? '600' : '400' }}>All</span>
          </div>
          
          {/* Pure Veg */}
          <div
            onClick={() => setFilter('Pure Veg')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-pure-veg"
          >
            <span style={{ fontSize: '24px' }}>🥗</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'Pure Veg' ? '#EA580C' : '#374151', fontWeight: filter === 'Pure Veg' ? '600' : '400' }}>Pure Veg</span>
          </div>
          
          {/* Non-Veg */}
          <div
            onClick={() => setFilter('Non-Veg')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-non-veg"
          >
            <span style={{ fontSize: '24px' }}>🍗</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'Non-Veg' ? '#EA580C' : '#374151', fontWeight: filter === 'Non-Veg' ? '600' : '400' }}>Non-Veg</span>
          </div>
          
          {/* Open 24x7 */}
          <div
            onClick={() => setFilter('Open 24x7')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-open-24x7"
          >
            <span style={{ fontSize: '24px' }}>🕐</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'Open 24x7' ? '#EA580C' : '#374151', fontWeight: filter === 'Open 24x7' ? '600' : '400' }}>24x7</span>
          </div>
          
          {/* AC Dining */}
          <div
            onClick={() => setFilter('AC Dining')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-ac-dining"
          >
            <span style={{ fontSize: '24px' }}>❄️</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'AC Dining' ? '#EA580C' : '#374151', fontWeight: filter === 'AC Dining' ? '600' : '400' }}>AC Dining</span>
          </div>
          
          {/* Parking */}
          <div
            onClick={() => setFilter('Parking')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            data-testid="filter-parking"
          >
            <span style={{ fontSize: '24px' }}>🅿️</span>
            <span style={{ fontSize: '11px', marginTop: '4px', color: filter === 'Parking' ? '#EA580C' : '#374151', fontWeight: filter === 'Parking' ? '600' : '400' }}>Parking</span>
          </div>
        </div>

        {/* Results Header */}
        <div className="mt-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {filter === 'All' ? 'Nearby Dhabas' : filter}
          </h2>
          <span className="text-sm text-gray-500">
            {filteredDhabas.length} found
          </span>
        </div>

        {/* Loading State */}
        {(loading || isLoadingCurrent) && (
          <div className="mt-8 flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-500">
              {isLoadingCurrent ? 'Getting your location...' : 'Finding dhabas near you...'}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="mt-8 text-center py-12">
            <span className="text-4xl mb-4 block">😕</span>
            <p className="text-gray-600">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-full text-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Dhabas List */}
        {!loading && !isLoadingCurrent && !error && (
          <div className="mt-4">
            {filteredDhabas.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-4xl mb-4 block">🔍</span>
                <p className="text-gray-600">No dhabas found matching your criteria</p>
                <button 
                  onClick={() => { setFilter('All'); setSearchQuery(''); }}
                  className="mt-4 text-orange-500 text-sm font-medium"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              filteredDhabas.map((dhaba) => (
                <DhabaCard 
                  key={dhaba.id} 
                  dhaba={{
                    ...dhaba,
                    // Map API response to component expected format
                    cuisine: Array.isArray(dhaba.cuisine) 
                      ? dhaba.cuisine.join(' • ') 
                      : dhaba.cuisine,
                    price: dhaba.price_range || '₹₹',
                    isOpen: dhaba.is_open !== false,
                  }} 
                  onClick={() => onSelectDhaba && onSelectDhaba(dhaba)} 
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FoodScreen;
